import { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Image as ImageIcon,
  MapPin,
  Trash2,
  RotateCcw,
  Pencil,
  Plus,
  Camera,
  MessageSquare,
  MoreVertical,
  Trees,
  Ruler,
  Map,
  Calendar,
  PenTool,
  ChevronRight,
  Layers,
} from 'lucide-react';
import type { ParcelDto } from '@forest/contracts';
import { useAuthStore } from '@/stores/sessionStore';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { Button } from '@/components/ui/Button';
import {
  FilterCheckbox,
  FilterPanel,
  FilterToggleButton,
  PageActions,
  PageTitle,
  PageToolbar,
  StatStrip,
  useFilterPanel,
} from '@/components/ui/PageToolbar';
import { TablePagination } from '@/components/ui/TablePagination';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Dialog } from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from '@/components/ui/DropdownMenu';
import { useUiStore } from '@/stores/uiStore';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import {
  useDeleteParcel,
  useHardDeleteParcel,
  useParcelFacets,
  useParcels,
  useRestoreParcel,
  useParcelDetail,
} from '@/features/parcels/useParcels';
import {
  ParcelViewModal,
  type ParcelViewMode,
} from '@/features/parcels/ParcelViewModal';
import { ParcelFormDialog } from '@/features/parcels/ParcelFormDialog';
import { useParcelPhotos } from '@/features/photos/usePhotos';
import { photoHref } from '@/features/photos/photoLink';
import { CommentsPanel } from '@/features/comments/CommentsPanel';
import { PolygonStatusBadge } from '@/features/photos/PolygonStatusBadge';
import { SortableHeader } from '@/components/ui/SortableHeader';
import { Select } from '@/components/ui/Select';
import { SubParcelList } from '@/features/subparcels/SubParcelList';
import { UnattachedSubParcelsDialog } from '@/features/subparcels/UnattachedSubParcelsDialog';
import { useUnattachedSubParcelCount } from '@/features/subparcels/useSubParcels';
import type { EditorMode } from '@/features/parcels/geometry-editor/editorTypes';

// Lazy-loaded: the Geoman + turf bundle only ships when the editor is opened.
const GeometryEditorDialog = lazy(() =>
  import('@/features/parcels/geometry-editor/GeometryEditorDialog').then((m) => ({
    default: m.GeometryEditorDialog,
  })),
);

export function ParcelsPage() {
  const { t } = useTranslation(['database', 'common', 'photos', 'comments']);
  /* Filters, paging and the open dialog all live in the URL.
     They used to be local state, so leaving the page (following a photo
     into Baza de date, say) and pressing Back dropped every filter and
     closed whatever was open, landing the user on page 1 of an
     unfiltered table. Keeping them in the query string makes Back
     restore the view, and makes the view shareable as a side effect.

     Filter edits `replace` so typing does not fill the history stack;
     opening a dialog pushes, so Back closes it the way a modal should. */
  const [searchParams, setSearchParams] = useSearchParams();
  const csv = (key: string) => {
    const raw = searchParams.get(key);
    return raw ? raw.split(',').filter(Boolean) : [];
  };
  const [page, setPage] = useState(() => Number(searchParams.get('page') ?? 1) || 1);
  const [limit, setLimit] = useState(() => Number(searchParams.get('limit') ?? 50) || 50);
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [raioane, setRaioane] = useState<string[]>(() => csv('raioane'));
  const [years, setYears] = useState<string[]>(() => csv('years'));
  const [ocoaleSilvice, setOcoaleSilvice] = useState<string[]>(() => csv('ocoale'));
  const [primarii, setPrimarii] = useState<string[]>(() => csv('primarii'));
  const [entitatiSilvice, setEntitatiSilvice] = useState<string[]>(() => csv('entitati'));
  const [codProiecte, setCodProiecte] = useState<string[]>(() => csv('coduri'));
  const [ucfs, setUcfs] = useState<string[]>(() => csv('ucfs'));
  const [includeDeleted, setIncludeDeleted] = useState(
    () => searchParams.get('deleted') === '1',
  );
  /** '' = all, 'with' = only polygons that have subpolygons, 'without'. */
  const [subFilter, setSubFilter] = useState<'' | 'with' | 'without'>(() => {
    const v = searchParams.get('sub');
    return v === 'with' || v === 'without' ? v : '';
  });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [unattachedOpen, setUnattachedOpen] = useState(false);
  const unattachedCount = useUnattachedSubParcelCount();
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  /** Filters collapse at every breakpoint, not just on phones — the
   *  table is the point of the page. Remembered per browser. */
  const { open: filtersOpen, toggle: toggleFilters } = useFilterPanel(
    'forest.ui.parcels.filters',
  );
  const activeFilterCount =
    (raioane.length > 0 ? 1 : 0) +
    (years.length > 0 ? 1 : 0) +
    (ocoaleSilvice.length > 0 ? 1 : 0) +
    (primarii.length > 0 ? 1 : 0) +
    (entitatiSilvice.length > 0 ? 1 : 0) +
    (codProiecte.length > 0 ? 1 : 0) +
    (ucfs.length > 0 ? 1 : 0) +
    (subFilter ? 1 : 0) +
    (includeDeleted ? 1 : 0);
  type ParcelSort =
    | 'createdAt'
    | 'anul'
    | 'raion'
    | 'primarie'
    | 'suprafata'
    | 'ocolSilvic'
    | 'entitateSilvica'
    | 'codProiect'
    | 'ucf';
  const [sort, setSort] = useState<ParcelSort>(
    () => (searchParams.get('sort') as ParcelSort) || 'createdAt',
  );
  const [order, setOrder] = useState<'asc' | 'desc'>(
    () => (searchParams.get('order') === 'asc' ? 'asc' : 'desc'),
  );
  const onSort = (next: { sort: ParcelSort; order: 'asc' | 'desc' }) => {
    setPage(1);
    setSort(next.sort);
    setOrder(next.order);
  };

  const viewParcelId = searchParams.get('view');
  const modalMode: ParcelViewMode =
    searchParams.get('mode') === 'image' ? 'image' : 'map';
  const photosParcelId = searchParams.get('photos');
  const commentsParcelId = searchParams.get('comments');
  const [parcelToDelete, setParcelToDelete] = useState<ParcelDto | null>(null);
  const [parcelToHardDelete, setParcelToHardDelete] = useState<ParcelDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formParcel, setFormParcel] = useState<ParcelDto | null>(null);
  const [formStartWithEditor, setFormStartWithEditor] = useState(false);
  const [editorParcel, setEditorParcel] = useState<ParcelDto | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>('edit');

  const me = useAuthStore((s) => s.user);
  const canEditGeometry = (row: ParcelDto) => {
    if (!me) return false;
    if (me.role === 'SUPER_ADMIN') return true;
    if (me.role === 'ADMINISTRATOR') return row.userId === me.id;
    return false;
  };

  /* Serialise the filter state into the query string. `replace` so a
     keystroke does not add a history entry; the dialog helpers below
     push instead. Only writes when the string actually changes, or the
     effect would loop on its own update. */
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDrop = (key: string, value: string) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    setOrDrop('search', search);
    setOrDrop('raioane', raioane.join(','));
    setOrDrop('years', years.join(','));
    setOrDrop('ocoale', ocoaleSilvice.join(','));
    setOrDrop('primarii', primarii.join(','));
    setOrDrop('entitati', entitatiSilvice.join(','));
    setOrDrop('coduri', codProiecte.join(','));
    setOrDrop('ucfs', ucfs.join(','));
    setOrDrop('deleted', includeDeleted ? '1' : '');
    setOrDrop('sub', subFilter);
    setOrDrop('page', page > 1 ? String(page) : '');
    setOrDrop('limit', limit !== 50 ? String(limit) : '');
    setOrDrop('sort', sort !== 'createdAt' ? sort : '');
    setOrDrop('order', order !== 'desc' ? order : '');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    search, raioane, years, ocoaleSilvice, primarii, entitatiSilvice,
    codProiecte, ucfs, includeDeleted, subFilter, page, limit, sort, order,
    searchParams, setSearchParams,
  ]);

  /** Open a dialog by putting it in the URL, so Back closes it. */
  const openDialog = useCallback(
    (key: 'view' | 'photos' | 'comments', id: string, mode?: ParcelViewMode) => {
      const next = new URLSearchParams(searchParams);
      next.delete('view');
      next.delete('photos');
      next.delete('comments');
      next.delete('mode');
      next.set(key, id);
      if (key === 'view' && mode) next.set('mode', mode);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const closeDialog = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    next.delete('photos');
    next.delete('comments');
    next.delete('mode');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const facets = useParcelFacets();
  const deleteMut = useDeleteParcel();
  const restoreMut = useRestoreParcel();
  const hardDeleteMut = useHardDeleteParcel();
  const isSuperAdmin = me?.role === 'SUPER_ADMIN';
  const pushToast = useUiStore((s) => s.pushToast);
  const params = useMemo(
    () => ({
      page,
      limit,
      search: search || undefined,
      raioane: raioane.length > 0 ? raioane.join(',') : undefined,
      ocoaleSilvice: ocoaleSilvice.length > 0 ? ocoaleSilvice.join(',') : undefined,
      primarii: primarii.length > 0 ? primarii.join(',') : undefined,
      entitatiSilvice:
        entitatiSilvice.length > 0 ? entitatiSilvice.join(',') : undefined,
      codProiecte: codProiecte.length > 0 ? codProiecte.join(',') : undefined,
      ucfs: ucfs.length > 0 ? ucfs.join(',') : undefined,
      years: years.length > 0 ? years.join(',') : undefined,
      includeDeleted: includeDeleted || undefined,
      hasSubParcels: subFilter === '' ? undefined : subFilter === 'with',
      sort,
      order,
    }),
    [
      page,
      limit,
      search,
      raioane,
      ocoaleSilvice,
      primarii,
      entitatiSilvice,
      codProiecte,
      ucfs,
      years,
      includeDeleted,
      subFilter,
      sort,
      order,
    ],
  );
  const query = useParcels(params);

  const rows = query.data?.items ?? [];
  /* The view modal needs the whole polygon (geometry included). It is
     normally the row already on screen; after a Back it may not be on
     the restored page, so fall back to fetching it by id. */
  const rowById = rows.find((r) => r.id === viewParcelId) ?? null;
  const fetchedParcel = useParcelDetail(viewParcelId && !rowById ? viewParcelId : null);
  const modalParcel = rowById ?? fetchedParcel.data ?? null;
  const totals = query.data?.totals;
  const pag = query.data?.pagination;

  // ParcelsPage queries the list endpoint without `lite=true`, so
  // every row carries its full geometry inline — the Harta / Imagine
  // modal and the geometry editor get the polygon immediately, no
  // second roundtrip. (FilterPage opts into lite mode because its map
  // runs on MVT tiles and the results table doesn't need geometry.)

  const openModal = (p: ParcelDto, mode: ParcelViewMode) =>
    openDialog('view', p.id, mode);

  const openCreateForm = () => {
    setFormParcel(null);
    // Geometry-first flow: eligible users (SUPER_ADMIN + ADMINISTRATOR)
    // land in the map editor first; on save the text form opens with
    // suprafața + raion + primăria + profile-default fields pre-filled.
    const canDraw = me?.role === 'SUPER_ADMIN' || me?.role === 'ADMINISTRATOR';
    setFormStartWithEditor(canDraw);
    setFormOpen(true);
  };

  const openEditForm = (p: ParcelDto) => {
    setFormParcel(p);
    setFormStartWithEditor(false);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!parcelToDelete) return;
    const label = `${parcelToDelete.raion} · ${parcelToDelete.primarie} · UCF ${parcelToDelete.ucf}`;
    try {
      await deleteMut.mutateAsync(parcelToDelete.id);
      setParcelToDelete(null);
      pushToast({ variant: 'success', message: t('database:delete.success', { label }) });
    } catch (e) {
      pushToast({
        variant: 'error',
        message: t('database:delete.failed', { message: (e as Error).message }),
      });
    }
  };

  const handleRestore = async (p: ParcelDto) => {
    const label = `${p.raion} · ${p.primarie} · UCF ${p.ucf}`;
    try {
      await restoreMut.mutateAsync(p.id);
      pushToast({ variant: 'success', message: t('database:restore.success', { label }) });
    } catch (e) {
      pushToast({
        variant: 'error',
        message: t('database:restore.failed', { message: (e as Error).message }),
      });
    }
  };

  const confirmHardDelete = async () => {
    if (!parcelToHardDelete) return;
    const label = `${parcelToHardDelete.raion} · ${parcelToHardDelete.primarie} · UCF ${parcelToHardDelete.ucf}`;
    try {
      await hardDeleteMut.mutateAsync(parcelToHardDelete.id);
      setParcelToHardDelete(null);
      pushToast({
        variant: 'success',
        message: t('database:hardDelete.success', { label }),
      });
    } catch (e) {
      pushToast({
        variant: 'error',
        message: t('database:hardDelete.failed', { message: (e as Error).message }),
      });
    }
  };

  return (
    /* Flex column that exactly fills <main>; the table card below
       grabs the remaining height (flex-1 + min-h-0) and scrolls
       internally, leaving title + stats + filters fixed at the top.
       `min-h-0` is required on the column AND on the table card so
       flex doesn't fall back to content-height (which would defeat
       the internal scroll). */
    <div className="animate-fade-in flex h-full min-h-0 flex-col gap-2">
      {/* Row 1: title + inline stats + actions. */}
      <PageToolbar>
        <PageTitle title={t('database:page.title')} hint={t('database:page.subtitle')} />
        {totals && pag ? (
          <StatStrip
            items={[
              {
                label: t('database:stats.poligoane'),
                value: pag.total.toLocaleString('ro-MD'),
                icon: Trees,
                color: 'emerald',
              },
              {
                label: t('database:stats.suprafataHa'),
                value: totals.totalSuprafata.toLocaleString('ro-MD', {
                  maximumFractionDigits: 2,
                }),
                icon: Ruler,
                color: 'sky',
              },
              {
                label: t('database:stats.distinctRaioane'),
                value: totals.distinctRaioane,
                icon: Map,
                color: 'amber',
              },
              {
                label: t('database:stats.distinctAni'),
                value: totals.distinctAni,
                icon: Calendar,
                color: 'violet',
              },
            ]}
          />
        ) : null}
        <PageActions>
          {(unattachedCount.data?.count ?? 0) > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setUnattachedOpen(true)}
              title={t('database:subparcels.unattachedHint', {
                defaultValue:
                  'Aceste subpoligoane nu se află în niciun poligon importat.',
              })}
            >
              <Layers className="h-3.5 w-3.5 text-orange-600" />
              {t('database:subparcels.unattachedButton', {
                defaultValue: 'Fără poligon ({{count}})',
                count: unattachedCount.data?.count ?? 0,
              })}
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" onClick={openCreateForm}>
            <Plus className="h-3.5 w-3.5" />
            {t('database:page.newButton')}
          </Button>
          <Link to="/app/import">
            <Button size="sm">{t('database:page.importButton')}</Button>
          </Link>
        </PageActions>
      </PageToolbar>

      {/* Row 2: search + filter toggle. The filter grid below is
          collapsed by default at every breakpoint (state persisted) so
          the table keeps the vertical space on small monitors. */}
      <div className="flex items-center gap-2">
        <Input
          uiSize="sm"
          className="flex-1"
          placeholder={t('database:filters.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <FilterToggleButton
          open={filtersOpen}
          count={activeFilterCount}
          onClick={toggleFilters}
          label={t('common:action.filters')}
        />
      </div>

      {filtersOpen ? (
        <FilterPanel className="xl:grid-cols-8">
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.allRaioane')}
            countLabel={(n) => t('photos:filters.raioaneSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={raioane}
            onChange={(next) => {
              setPage(1);
              setRaioane(next);
            }}
            options={(facets.data?.raioane ?? []).map((r) => ({ value: r, label: r }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.allOcoale')}
            countLabel={(n) => t('database:filters.ocoaleSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={ocoaleSilvice}
            onChange={(next) => {
              setPage(1);
              setOcoaleSilvice(next);
            }}
            options={[
              ...(facets.data?.hasNullOcolSilvic
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.ocoaleSilvice ?? []).map((o) => ({ value: o, label: o })),
            ]}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.allAni')}
            countLabel={(n) => t('photos:filters.aniSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={years}
            onChange={(next) => {
              setPage(1);
              setYears(next);
            }}
            options={(facets.data?.ani ?? []).map((a) => ({
              value: String(a),
              label: String(a),
            }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.allPrimarii', {
              defaultValue: 'Toate primăriile',
            })}
            countLabel={(n) =>
              t('database:filters.primariiSelected', { count: n })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={primarii}
            onChange={(next) => {
              setPage(1);
              setPrimarii(next);
            }}
            options={(facets.data?.primarii ?? []).map((p) => ({
              value: p,
              label: p,
            }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.allEntitatiSilvice', {
              defaultValue: 'Toate entitățile silvice',
            })}
            countLabel={(n) =>
              t('database:filters.entitatiSelected', { count: n })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={entitatiSilvice}
            onChange={(next) => {
              setPage(1);
              setEntitatiSilvice(next);
            }}
            options={[
              ...(facets.data?.hasNullEntitateSilvica
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.entitateSilvice ?? []).map((e) => ({
                value: e,
                label: e,
              })),
            ]}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.codProiectPlaceholder', {
              defaultValue: 'Cod proiect',
            })}
            countLabel={(n) =>
              t('database:filters.codProiectSelected', {
                count: n,
                defaultValue: '{{count}} selected',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={codProiecte}
            onChange={(next) => {
              setPage(1);
              setCodProiecte(next);
            }}
            options={(facets.data?.codProiecte ?? []).map((c) => ({ value: c, label: c }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.ucfPlaceholder', {
              defaultValue: 'UCF',
            })}
            countLabel={(n) =>
              t('database:filters.ucfSelected', {
                count: n,
                defaultValue: '{{count}} selected',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={ucfs}
            onChange={(next) => {
              setPage(1);
              setUcfs(next);
            }}
            options={(facets.data?.ucfs ?? []).map((u) => ({ value: u, label: u }))}
          />
          <div className="w-full sm:w-48">
            <Select
              uiSize="sm"
              aria-label={t('database:subparcels.filterLabel', { defaultValue: 'Subpoligoane' })}
              value={subFilter}
              onChange={(e) => {
                setPage(1);
                setSubFilter(e.target.value as '' | 'with' | 'without');
              }}
            >
              <option value="">
                {t('database:subparcels.filterAll', { defaultValue: 'Subpoligoane: toate' })}
              </option>
              <option value="with">
                {t('database:subparcels.filterWith', { defaultValue: 'Cu subpoligoane' })}
              </option>
              <option value="without">
                {t('database:subparcels.filterWithout', { defaultValue: 'Fără subpoligoane' })}
              </option>
            </Select>
          </div>
          <FilterCheckbox
            checked={includeDeleted}
            onChange={(next) => {
              setPage(1);
              setIncludeDeleted(next);
            }}
            label={t('database:filters.includeDeleted')}
          />
        </FilterPanel>
      ) : null}

      <Card className="flex flex-1 min-h-0 flex-col">
        <div className="flex-1 min-h-0 overflow-auto px-2 pb-2">
          {query.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Spinner /> {t('state.loading')}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('database:empty.title')}
              description={t('database:empty.description')}
            />
          ) : (
            <>
            {/* Mobile cards */}
            <div className="space-y-2 pt-2 md:hidden">
              {rows.map((r) => {
                const isDeleted = r.deletedAt !== null;
                return (
                  <div
                    key={r.id}
                    className={
                      isDeleted
                        ? 'rounded-lg border border-red-200 bg-red-50/40 p-3 text-slate-400 line-through'
                        : 'rounded-lg border border-slate-200 bg-white p-3 shadow-sm'
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {r.raion} · {r.primarie}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {t('database:table.header.codProiect')}:{' '}
                          <span className="font-mono">{r.codProiect ?? '—'}</span>
                          {' · UCF '}
                          <span className="font-mono">{r.ucf}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {r.suprafata.toFixed(2)} ha · {r.anul}
                        </p>
                        {(r.subParcelCount ?? 0) > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(r.id)}
                            aria-expanded={expanded.has(r.id)}
                            className="mt-1 inline-flex items-center gap-0.5 text-xs font-semibold text-orange-700"
                          >
                            <ChevronRight
                              className={
                                'h-3.5 w-3.5 transition-transform ' +
                                (expanded.has(r.id) ? 'rotate-90' : '')
                              }
                            />
                            {t('database:subparcels.expand', {
                              defaultValue: '{{count}} subpoligoane',
                              count: r.subParcelCount ?? 0,
                            })}
                          </button>
                        ) : null}
                        {expanded.has(r.id) ? (
                          <div className="mt-2 overflow-x-auto">
                            <SubParcelList parcelId={r.id} bare />
                          </div>
                        ) : null}
                      </div>
                      <DropdownMenu>
                        <DropdownTrigger>
                          <MoreVertical className="h-4 w-4" />
                        </DropdownTrigger>
                        <DropdownContent align="end">
                          <DropdownItem
                            icon={ImageIcon}
                            label={t('database:action.image')}
                            onClick={() => openModal(r, 'image')}
                          />
                          <DropdownItem
                            icon={MapPin}
                            label={t('database:action.map')}
                            onClick={() => openModal(r, 'map')}
                          />
                          <DropdownItem
                            icon={Camera}
                            label={t('database:action.photos')}
                            onClick={() => openDialog('photos', r.id)}
                          />
                          <DropdownItem
                            icon={MessageSquare}
                            label={t('database:action.comments')}
                            onClick={() => openDialog('comments', r.id)}
                          />
                          <DropdownSeparator />
                          {isDeleted ? (
                            <>
                              <DropdownItem
                                icon={RotateCcw}
                                label={t('database:action.restore')}
                                onClick={() => void handleRestore(r)}
                                disabled={restoreMut.isPending}
                              />
                              {isSuperAdmin ? (
                                <DropdownItem
                                  icon={Trash2}
                                  label={t('database:action.hardDelete')}
                                  onClick={() => setParcelToHardDelete(r)}
                                  destructive
                                />
                              ) : null}
                            </>
                          ) : (
                            <>
                              <DropdownItem
                                icon={Pencil}
                                label={t('database:action.edit')}
                                onClick={() => openEditForm(r)}
                              />
                              <DropdownItem
                                icon={PenTool}
                                label={
                                  canEditGeometry(r)
                                    ? t('database:editor.editGeometryAction')
                                    : t('database:editor.viewGeometryAction')
                                }
                                onClick={() => {
                                  setEditorParcel(r);
                                  setEditorMode(canEditGeometry(r) ? 'edit' : 'readonly');
                                }}
                              />
                              <DropdownItem
                                icon={Trash2}
                                label={t('database:action.delete')}
                                onClick={() => setParcelToDelete(r)}
                                destructive
                              />
                            </>
                          )}
                        </DropdownContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* No own scrollport here: the card body div is the scroll container,
                so the sticky thead anchors to it and stays fixed. An
                inner overflow-auto would capture the stickiness in a
                wrapper that never scrolls, letting the header slide away. */}
            <div className="hidden md:block">
              <table className="table-striped table-sticky w-full text-sm dark:text-slate-300">
                <thead className="bg-slate-50/95 text-xs uppercase text-slate-500 backdrop-blur-sm dark:bg-slate-800/95 dark:text-slate-400">
                  <tr>
                    <th className="w-10 px-1 py-2" aria-label={t('database:subparcels.layerName', { defaultValue: 'Subpoligoane' })} />
                    <SortableHeader<ParcelSort>
                      field="raion"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('database:table.header.raion')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="primarie"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('database:table.header.primarie')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="entitateSilvica"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('database:table.header.entitateSilvica')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="ocolSilvic"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('database:table.header.ocolSilvic')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="codProiect"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('database:table.header.codProiect')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="ucf"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('database:table.header.ucf')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="suprafata"
                      current={sort}
                      order={order}
                      onSort={onSort}
                      defaultOrder="desc"
                      align="right"
                    >
                      {t('database:table.header.suprafata')}
                    </SortableHeader>
                    <SortableHeader<ParcelSort>
                      field="anul"
                      current={sort}
                      order={order}
                      onSort={onSort}
                      defaultOrder="desc"
                      align="right"
                    >
                      {t('database:table.header.anul')}
                    </SortableHeader>
                    <th className="w-12 px-2 py-2 text-center font-semibold text-xs uppercase text-slate-500 dark:text-slate-400">{t('database:table.header.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isDeleted = r.deletedAt !== null;
                    const subCount = r.subParcelCount ?? 0;
                    const isOpen = expanded.has(r.id);
                    return (
                      <Fragment key={r.id}>
                      <tr
                        className={
                          isDeleted
                            ? 'border-t border-slate-100 bg-red-50/40 text-slate-400 line-through'
                            : 'border-t border-slate-100 transition-colors hover:bg-forest-50/40'
                        }
                      >
                        <td className="px-1 py-1.5 text-center no-underline">
                          {subCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(r.id)}
                              aria-expanded={isOpen}
                              title={t('database:subparcels.expand', {
                                defaultValue: '{{count}} subpoligoane',
                                count: subCount,
                              })}
                              className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] font-semibold text-orange-700 hover:bg-orange-50"
                            >
                              <ChevronRight
                                className={'h-3.5 w-3.5 transition-transform ' + (isOpen ? 'rotate-90' : '')}
                              />
                              {subCount}
                            </button>
                          ) : null}
                        </td>
                        <td className="px-3 py-1.5">{r.raion}</td>
                        <td className="px-3 py-1.5">{r.primarie}</td>
                        <td className="px-3 py-1.5 text-slate-600">
                          {r.entitateSilvica ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">{r.ocolSilvic ?? '\u2014'}</td>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-600">
                          {r.codProiect ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.ucf}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">
                          {r.suprafata.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">{r.anul}</td>
                        <td className="px-2 py-1.5 text-center no-underline">
                          <DropdownMenu>
                            <DropdownTrigger>
                              <MoreVertical className="h-4 w-4" />
                            </DropdownTrigger>
                            <DropdownContent align="end">
                              <DropdownItem
                                icon={ImageIcon}
                                label={t('database:action.image')}
                                onClick={() => openModal(r, 'image')}
                              />
                              <DropdownItem
                                icon={MapPin}
                                label={t('database:action.map')}
                                onClick={() => openModal(r, 'map')}
                              />
                              <DropdownItem
                                icon={Camera}
                                label={t('database:action.photos')}
                                onClick={() => openDialog('photos', r.id)}
                              />
                              <DropdownItem
                                icon={MessageSquare}
                                label={t('database:action.comments')}
                                onClick={() => openDialog('comments', r.id)}
                              />
                              <DropdownSeparator />
                              {isDeleted ? (
                                <DropdownItem
                                  icon={RotateCcw}
                                  label={t('database:action.restore')}
                                  onClick={() => void handleRestore(r)}
                                  disabled={restoreMut.isPending}
                                />
                              ) : (
                                <>
                                  <DropdownItem
                                    icon={Pencil}
                                    label={t('database:action.edit')}
                                    onClick={() => openEditForm(r)}
                                  />
                                  <DropdownItem
                                    icon={PenTool}
                                    label={
                                      canEditGeometry(r)
                                        ? t('database:editor.editGeometryAction')
                                        : t('database:editor.viewGeometryAction')
                                    }
                                    onClick={() => {
                                      setEditorParcel(r);
                                      setEditorMode(
                                        canEditGeometry(r) ? 'edit' : 'readonly',
                                      );
                                    }}
                                  />
                                  <DropdownItem
                                    icon={Trash2}
                                    label={t('database:action.delete')}
                                    onClick={() => setParcelToDelete(r)}
                                    destructive
                                  />
                                </>
                              )}
                            </DropdownContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="bg-orange-50/40 dark:bg-orange-900/10">
                          <td />
                          <td colSpan={9} className="px-3 py-2">
                            <SubParcelList parcelId={r.id} bare />
                          </td>
                        </tr>
                      ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
        {/* Outside the scroll container and never gated on `pag`: the pager
            and the rows-per-page select stay pinned to the bottom of the
            card instead of sitting 200 rows down. */}
        <TablePagination
          className="border-t border-slate-200 px-2 py-1.5 dark:border-slate-700"
          page={page}
          limit={limit}
          total={pag?.total ?? 0}
          totalPages={pag?.totalPages ?? 1}
          onPageChange={setPage}
          onLimitChange={(next) => {
            setPage(1);
            setLimit(next);
          }}
          rowsLabel={t('common:common.rows')}
          prevLabel={t('common:pagination.prev')}
          nextLabel={t('common:pagination.next')}
        />
      </Card>

      <UnattachedSubParcelsDialog
        open={unattachedOpen}
        onClose={() => setUnattachedOpen(false)}
      />

      <ParcelViewModal
        open={modalParcel !== null}
        mode={modalMode}
        parcel={modalParcel}
        onClose={closeDialog}
      />

      <ParcelFormDialog
        open={formOpen}
        parcel={formParcel}
        startWithEditor={formStartWithEditor}
        onClose={() => setFormOpen(false)}
      />

      {editorParcel ? (
        <Suspense fallback={<EditorLoading />}>
          <GeometryEditorDialog
            open={editorParcel !== null}
            mode={editorMode}
            parcel={editorParcel}
            onClose={() => setEditorParcel(null)}
          />
        </Suspense>
      ) : null}

      {/* Parcel photos dialog */}
      <Dialog
        open={photosParcelId !== null}
        onClose={closeDialog}
        title={t('photos:parcelPhotos.title', { count: 0 })}
        size="xl"
      >
        {photosParcelId ? (
          <ParcelPhotosContent parcelId={photosParcelId} onClose={closeDialog} />
        ) : null}
      </Dialog>

      {/* Parcel comments dialog */}
      <Dialog
        open={commentsParcelId !== null}
        onClose={closeDialog}
        title={t('comments:title')}
        size="lg"
      >
        {commentsParcelId ? (
          <CommentsPanel entityType="Parcel" entityId={commentsParcelId} />
        ) : null}
      </Dialog>

      <Dialog
        open={parcelToDelete !== null}
        onClose={() => {
          if (!deleteMut.isPending) setParcelToDelete(null);
        }}
        title={t('database:delete.confirmTitle')}
        size="md"
      >
        {parcelToDelete ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{t('database:delete.confirmBody')}</p>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="font-semibold text-slate-900">
                {parcelToDelete.raion} · {parcelToDelete.primarie}
              </div>
              <div className="text-xs text-slate-600">
                Cod: <code className="font-mono">{parcelToDelete.codProiect ?? '\u2014'}</code>
                {' · '}UCF <code className="font-mono">{parcelToDelete.ucf}</code>
                {' · '}Supr. {parcelToDelete.suprafata.toFixed(2)} ha
                {' · '}An {parcelToDelete.anul}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setParcelToDelete(null)}
                disabled={deleteMut.isPending}
              >
                {t('database:delete.cancel')}
              </Button>
              <Button
                onClick={() => void confirmDelete()}
                loading={deleteMut.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('database:delete.confirm')}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      {/* Hard-delete (permanent) — SUPER_ADMIN only. The trigger is gated
          in the action menu, but we keep the dialog mounted unconditionally
          so the close animation works after the row disappears. */}
      <Dialog
        open={parcelToHardDelete !== null}
        onClose={() => {
          if (!hardDeleteMut.isPending) setParcelToHardDelete(null);
        }}
        title={t('database:hardDelete.confirmTitle')}
        size="md"
      >
        {parcelToHardDelete ? (
          <div className="space-y-4">
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/40">
              <p className="font-semibold text-red-700 dark:text-red-300">
                {t('database:hardDelete.warningTitle')}
              </p>
              <p className="mt-1 text-red-700 dark:text-red-300">
                {t('database:hardDelete.warningBody')}
              </p>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800">
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                {parcelToHardDelete.raion} · {parcelToHardDelete.primarie}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Cod: <code className="font-mono">{parcelToHardDelete.codProiect ?? '—'}</code>
                {' · '}UCF <code className="font-mono">{parcelToHardDelete.ucf}</code>
                {' · '}Supr. {parcelToHardDelete.suprafata.toFixed(2)} ha
                {' · '}An {parcelToHardDelete.anul}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setParcelToHardDelete(null)}
                disabled={hardDeleteMut.isPending}
              >
                {t('database:hardDelete.cancel')}
              </Button>
              <Button
                onClick={() => void confirmHardDelete()}
                loading={hardDeleteMut.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('database:hardDelete.confirm')}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function ParcelPhotosContent({
  parcelId,
  onClose: _onClose,
}: {
  parcelId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(['photos']);
  const query = useParcelPhotos(parcelId);
  const photos = query.data ?? [];
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
        <Spinner />
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <p className="py-4 text-sm text-slate-400">{t('photos:parcelPhotos.empty')}</p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600">
        {t('photos:parcelPhotos.title', { count: photos.length })}
      </p>
      <div className="max-h-80 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1 text-center font-semibold">{t('photos:table.header.photo')}</th>
              <th className="px-2 py-1 text-left font-semibold">{t('photos:table.header.fileName')}</th>
              <th className="px-2 py-1 text-left font-semibold">{t('photos:table.header.author')}</th>
              <th className="px-2 py-1 text-right font-semibold">{t('photos:table.header.anul')}</th>
              <th className="px-2 py-1 text-left font-semibold">{t('photos:table.header.speciaDeBaza')}</th>
              <th className="px-2 py-1 text-right font-semibold">{t('photos:table.header.anulCultura')}</th>
              <th className="px-2 py-1 text-left font-semibold">{t('photos:table.header.ultimaLucrare')}</th>
              <th className="px-2 py-1 text-right font-semibold">{t('photos:table.header.latitude')}</th>
              <th className="px-2 py-1 text-right font-semibold">{t('photos:table.header.longitude')}</th>
              <th className="px-2 py-1 text-left font-semibold">{t('photos:table.header.status')}</th>
            </tr>
          </thead>
          <tbody>
            {photos.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1 text-center">
                  {p.downloadUrl ? (
                    <button
                      type="button"
                      onClick={() => setLightboxUrl(toViewUrl(p.downloadUrl))}
                      className="inline-block rounded border border-slate-200 p-0.5 hover:border-forest-400 hover:shadow-sm transition-shadow"
                    >
                      <img
                        src={toThumbnailUrl(p.downloadUrl)}
                        alt={p.fileName}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="h-10 w-10 rounded object-cover bg-slate-100"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </button>
                  ) : (
                    '\u2014'
                  )}
                </td>
                <td className="px-2 py-1">
                  <Link
                    to={photoHref(p.id)}
                    className="text-forest-700 hover:underline"
                    title={t('photos:map.openInDatabase', {
                      defaultValue: 'Deschide în Baza de date',
                    })}
                  >
                    <span className="font-mono text-xs text-slate-500">#{p.nr}</span>{' '}
                    {p.fileName}
                  </Link>
                </td>
                <td className="px-2 py-1">
                  {p.author ? (p.author.cod ?? p.author.fullName) : '—'}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {p.year
                    ? `${p.month ? `${String(p.month).padStart(2, '0')}.` : ''}${p.year}`
                    : '—'}
                </td>
                <td className="px-2 py-1">{p.speciaDeBaza ?? '—'}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {p.anulCultura ?? '—'}
                </td>
                <td className="px-2 py-1">
                  <span
                    className="block max-w-[180px] truncate"
                    title={p.ultimaLucrare ?? undefined}
                  >
                    {p.ultimaLucrare ?? '—'}
                  </span>
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{p.latitude.toFixed(6)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{p.longitude.toFixed(6)}</td>
                <td className="px-2 py-1">
                  <PolygonStatusBadge status={p.polygonStatus} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Lightbox */}
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
}

/**
 * Convert a Google Drive download URL to a thumbnail URL.
 * Input:  https://drive.google.com/uc?id=FILE_ID&export=download
 * Output: https://drive.google.com/thumbnail?id=FILE_ID&sz=w100
 */
function toThumbnailUrl(url: string): string {
  const match = /[?&]id=([^&]+)/.exec(url);
  if (match) return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w100`;
  return url;
}

function toViewUrl(url: string): string {
  const match = /[?&]id=([^&]+)/.exec(url);
  if (match) return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1200`;
  return url;
}

function EditorLoading() {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-white">
      <Spinner />
    </div>
  );
}

